import { c as _c } from "react/compiler-runtime";
import { IcoCircleDotted16, IcoWarning16 } from '@onefootprint/icons';
import type { ProblemMarker, TypedOnboardingData } from '@onefootprint/request-types/dashboard';
import { Tooltip } from '@onefootprint/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import TypeIcon from './type-icon';
type ExpressionTypeHintProps = {
  expressionType: TypedOnboardingData | undefined;
  problem: ProblemMarker | undefined;
  /** False for a half-written row, which has no type coming and so isn't reported as pending. */
  isTypeable?: boolean;
  /** Set when this key is published today at a type this expression no longer resolves to. */
  retyped?: string;
};

// The slot holds its footprint in every state, so a type landing doesn't shift the row it sits in.
const Slot = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <span className="flex size-4 shrink-0 items-center justify-center">{children}</span>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

// One accessible name for the whole glyph, so the icon inside stays decorative.
const Hint = t0 => {
  const $ = _c(6);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== label) {
    t1 = <span className="flex" role="img" aria-label={label}>{children}</span>;
    $[0] = children;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== label || $[4] !== t1) {
    t2 = <Slot><Tooltip content={label}>{t1}</Tooltip></Slot>;
    $[3] = label;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};

/** The type an expression resolves to, once the backend has resolved one. */
export const ExpressionTypeHint = t0 => {
  const $ = _c(21);
  const {
    expressionType,
    problem,
    isTypeable: t1,
    retyped
  } = t0;
  const isTypeable = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "expressionType"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t2);
  if (problem?.severity === "error" || !isTypeable) {
    let t3;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Slot />;
      $[1] = t3;
    } else {
      t3 = $[1];
    }
    return t3;
  }
  if (!expressionType) {
    let t3;
    if ($[2] !== t) {
      t3 = t(_temp);
      $[2] = t;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <IcoCircleDotted16 color="tertiary" />;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] !== t3) {
      t5 = <Hint label={t3}>{t4}</Hint>;
      $[5] = t3;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    return t5;
  }
  if (retyped) {
    let t3;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <IcoWarning16 color="warning" />;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== retyped) {
      t4 = <Hint label={retyped}>{t3}</Hint>;
      $[8] = retyped;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  if (problem?.severity === "warning") {
    let t3;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <IcoWarning16 color="warning" />;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== problem.message) {
      t4 = <Hint label={problem.message}>{t3}</Hint>;
      $[11] = problem.message;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return t4;
  }
  const [dataType] = expressionType.dataType;
  let t3;
  if ($[13] !== dataType || $[14] !== t) {
    t3 = t(_temp2, {
      dataType
    });
    $[13] = dataType;
    $[14] = t;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== dataType) {
    t4 = <TypeIcon type={dataType} />;
    $[16] = dataType;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== t3 || $[19] !== t4) {
    t5 = <Hint label={t3}>{t4}</Hint>;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
};
export default ExpressionTypeHint;
function _temp($) {
  return $.pending;
}
function _temp2($_0) {
  return $_0.resolved;
}